import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * MarginRuleHistory — read-only timeline of margin-rule CRUD events
 * for a single customer.
 *
 * Renders inside the CustomerDetail page, sandwiched between the
 * Margin Rules table and the Fuel Cards table. Each entry shows:
 *   - the action (created / updated / deleted)
 *   - the actor (admin email)
 *   - the timestamp (UTC, abbreviated)
 *   - on updates: the diff (which fields changed, before → after)
 *   - on create/delete: the rule's summary (network, country, scope,
 *     product, margin_cpl).
 *
 * Defaults to collapsed because the timeline can be long for active
 * customers; the operator opens it on-demand when investigating
 * pricing complaints or auditing changes.
 */
export default function MarginRuleHistory({ customerId, testIdPrefix = "margin-history" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/margins/history", {
        params: { customer_id: customerId, limit: 200 },
      });
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load margin history");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch every time the user opens the panel so it reflects any
  // CRUD that happened on the same page since last open.
  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  return (
    <div
      className="gf-card mb-6"
      data-testid={testIdPrefix}
    >
      <div className="gf-card-header flex items-center justify-between">
        <h3>Margin rule history</h3>
        <button
          className="gf-btn gf-btn-ghost"
          data-testid={`${testIdPrefix}-toggle`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide" : `Show ${rows.length || ""}`.trim()}
        </button>
      </div>
      {open && (
        <div className="gf-card-body">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
              No margin-rule changes recorded yet for this customer.
            </div>
          ) : (
            <ol className="space-y-3">
              {rows.map((r) => (
                <HistoryRow key={r.history_id} row={r} testIdPrefix={testIdPrefix} />
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}


function HistoryRow({ row, testIdPrefix }) {
  const ts = (row.created_at || "").slice(0, 19).replace("T", " ");
  const meta = useMemo(() => {
    // Pick the most-informative side of the snapshot for the
    // "summary" line. Updates → after; deletes → before; creates → after.
    const snap = row.after && Object.keys(row.after).length ? row.after : row.before || {};
    return {
      product: snap.product_canonical || snap.product || "—",
      country: snap.country || "—",
      network: snap.network || "—",
      scope:   snap.scope || "default",
      cpl:     snap.margin_cpl,
      mode:    snap.pricing_mode || "add",
    };
  }, [row]);

  const diff = useMemo(() => {
    if (row.action !== "update" || !row.before || !row.after) return [];
    const out = [];
    const keys = new Set([...Object.keys(row.before), ...Object.keys(row.after)]);
    for (const k of keys) {
      // Skip noise fields.
      if (["updated_at", "created_at", "margin_id"].includes(k)) continue;
      const b = row.before[k];
      const a = row.after[k];
      if (JSON.stringify(b) === JSON.stringify(a)) continue;
      out.push({ key: k, before: b, after: a });
    }
    return out;
  }, [row]);

  const badge = {
    create: ["bg-emerald-100 text-emerald-800", "created"],
    update: ["bg-amber-100 text-amber-800",     "updated"],
    delete: ["bg-rose-100 text-rose-800",       "deleted"],
  }[row.action] || ["bg-zinc-100 text-zinc-700", row.action];

  return (
    <li
      className="border-l-2 border-zinc-200 pl-3"
      data-testid={`${testIdPrefix}-row-${row.history_id}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${badge[0]}`}>
          {badge[1]}
        </span>
        <span className="font-mono text-xs text-zinc-500">{ts}</span>
        <span className="text-xs text-zinc-700">by</span>
        <span className="text-xs font-medium">{row.actor}</span>
      </div>
      <div className="text-xs text-zinc-600 mt-0.5">
        {meta.network} · {meta.country} · {meta.scope} · {meta.product}
        {meta.cpl !== undefined && meta.cpl !== null && (
          <span> · <span className="font-mono">{meta.mode}={meta.cpl}</span></span>
        )}
      </div>
      {diff.length > 0 && (
        <ul className="mt-1 text-xs text-zinc-700 space-y-0.5 pl-3 list-disc">
          {diff.map((d) => (
            <li key={d.key}>
              <span className="font-medium">{d.key}</span>:{" "}
              <span className="font-mono text-zinc-500">{fmt(d.before)}</span>
              {" → "}
              <span className="font-mono text-zinc-900">{fmt(d.after)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}


function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
